<script lang="ts">
    import type { Document, Field, Lead, Image, SiteVisit } from '@/types/types';
    import type Blocks from '@/schemas/public/Blocks';
    import type { DependencyMap } from '@/types/fieldDependency';
    import Modal from '@/components/Modal.svelte';
    import FormContainer from '@/components/Form/FormContainer.svelte';
    import FormBlock from '@/components/Form/FormBlock.svelte';
    import InlineEditable from '@/components/Inputs/InlineEditable.svelte';
    import LineItems from '@/components/LineItems.svelte';
    import RelatedDocuments from '@/components/documents/RelatedDocuments.svelte';
    import CreateDocumentModal from '@/components/documents/CreateDocumentModal.svelte';
    import Comments from '@/components/Comments.svelte';
    import SiteVisitsList from '@/components/siteVisits/SiteVisitsList.svelte';
    import SiteSurveyForm from '@/components/leads/SiteSurveyForm.svelte';
    import PhasesList from '@/components/phases/PhasesList.svelte';
    import Gallery from '@/components/Gallery.svelte';
    import { Button } from '@/components/ui/button';
    import { page, router, useForm } from '@inertiajs/svelte';
    import { transformToFlatStructure, transformToNestedStructure } from '@/lib/formTransformers';
    import { toast } from 'svelte-sonner';
    import { ExternalLink, Briefcase, Mail, Phone } from 'lucide-svelte';


    type Props = {
        show: boolean;
        lead: Lead;
        blocks: (Blocks & { fields: Field[] })[];
        documentBlocks?: (Blocks & { fields: Field[] })[];
        siteSurveyBlocks?: (Blocks & { fields: Field[] })[];
        siteSurveyDependencyMap?: DependencyMap;
        siteVisitBlocks?: (Blocks & { fields: Field[] })[];
        relatedDocuments?: Document[];
        siteSurveyImages?: Image[];
        siteVisits?: SiteVisit[];
        onLeadUpdated?: (updatedLead: Lead) => void;
    };

    let { show = $bindable(), lead, blocks, documentBlocks, relatedDocuments, siteSurveyImages = [], siteSurveyBlocks, siteSurveyDependencyMap = {}, siteVisitBlocks, siteVisits = [], onLeadUpdated }: Props = $props();

    function close() {
        show = false;
    }

    let inlineForm = $state(useForm({}));
    let editingFieldId: undefined | number = $state(undefined);
    let localBlocks = $state<(Blocks & { fields: Field[] })[]>([]);

    // Initialize form data whenever modal opens or lead data changes
    $effect(() => {
        // Only initialize if modal is shown
        if (!show) return;

        // This will re-run whenever lead changes (tracked dependency)
        const flattenedLead = transformToFlatStructure(lead as any);
        let inlineFormInner: Record<string, any> = {}

        // Filter out the phase_1 schema block — its fields are virtual write-only
        // inputs used by the create-lead modal. On the detail view, phases are
        // shown via the PhasesList component instead.
        localBlocks = blocks.filter(block => (block as any).key !== 'phase_1').map(block => ({
            ...block,
            fields: block.fields.map(field => {
                const fieldValue = flattenedLead[field.column_name];
                inlineFormInner[field.column_name] = fieldValue ?? null;

                // Determine value_label for related fields based on current lead data
                let valueLabel = field.value_label;
                if (field.ui_type === 'related' || field.ui_type === 'polymorphic') {
                    if (field.column_name === 'related_customer_id' || field.column_name === 'is_primary') {
                        valueLabel = lead.primary_customer ?? lead.primaryCustomer?.first_name ?? null;
                    }
                }

                // Return a new field object with the value and value_label set
                return {
                    ...field,
                    value: fieldValue ?? null,
                    value_label: valueLabel
                };
            })
        }));

        inlineForm = useForm(inlineFormInner);
    });

    let showCreateDocumentModal = $state(false);
    function openCreateDocumentModal() {
        showCreateDocumentModal = true;
    }

    const leadPhases = $derived((lead as any).phases ?? []);



    let titleBackgroundColor = $derived.by(() => {
        switch (lead.status) {
            case 'new':
                return 'new';
            case 'needs_survey':
                return 'needs-survey';
            case 'needs_pricing':
                return 'needs-pricing';
            case 'tendered':
                return 'tendered';
            case 'closed_won':
                return 'closed-won';
            case 'closed_lost':
                return 'closed-lost';
            case 'booked':
                return 'booked';
            case 'invoiced':
                return 'invoiced';
            case 'extra_hire':
                return 'extra-hire';
            case 'awaiting_takedown':
                return 'awaiting-takedown';
            default:
                return '';
        }
    });

</script>

{#if show}
    <Modal title="{lead.reference ? lead.reference + ' · ' : ''}{lead.first_name} {lead.last_name} {lead.address?.postcode}" onClose={close} size="large" titleBackgroundColor={titleBackgroundColor}>
        {#snippet headerActions()}
            {#if lead.converted_to_job_id}
                <Button
                    variant="ghost"
                    size="sm"
                    onclick={() => {
                        router.visit(route('jobs.show', { job: lead.converted_to_job_id }));
                    }}
                    class="flex items-center gap-2"
                >
                    <Briefcase class="h-4 w-4" />
                    View Job{#if lead.converted_to_job?.reference} ({lead.converted_to_job.reference}){/if}
                </Button>
            {/if}
            <Button
                variant="ghost"
                size="sm"
                onclick={() => {
                    router.visit(route('leads.show', { lead: lead.id }));
                }}
                class="flex items-center gap-2"
            >
                <ExternalLink class="h-4 w-4" />
                View Full Record
            </Button>
        {/snippet}
        <div class="lead-detail-content">
            <FormContainer>
                {#each localBlocks as block (block.id)}
                        <FormBlock
                            label={block.label}
                            collapsible={true}
                            defaultOpen={true}
                        >
                            {#each block.fields as field (field.id)}
                                <InlineEditable
                                    field={field.column_name === 'status' ? { ...field, editable: false } : field}
                                    bind:editingFieldId={editingFieldId}
                                    error={$inlineForm.errors[field.column_name]}
                                    onSave={(newValue) => {
                                        $inlineForm.processing = true;
                                        $inlineForm[field.column_name] = newValue;

                                        // Transform flat form data to nested structure
                                        const transformedData = transformToNestedStructure($inlineForm.data());
                                        router.put(route("leads.update", { lead }), transformedData, {
                                            preserveScroll: true,
                                            onSuccess: (data) => {
                                                editingFieldId = undefined;
                                                if(data.props.flash.updatedLead) {
                                                    lead = data.props.flash.updatedLead;

                                                    // Notify parent component of the update
                                                    if (onLeadUpdated) {
                                                        onLeadUpdated(lead);
                                                    }
                                                }
                                                toast.success('Lead updated successfully');
                                            },
                                            onError: (errors) => {
                                                $inlineForm.errors = errors;
                                                toast.error('Failed to update lead');
                                            },
                                             onFinish: () => {
                                                $inlineForm.processing = false;
                                            }
                                        });
                                    }}
                                    record={lead}
                                />
                            {/each}

                            <!-- Related Customers (shown after Customer Information block, excluding primary) -->
                            {@const nonPrimaryCustomers = lead.customers?.filter(c => !c.pivot?.is_primary) ?? []}
                            {#if block.label === 'Customer Information' && nonPrimaryCustomers.length > 0}
                                <div class="col-span-2 related-customers-section">
                                    <div class="related-customers-header">
                                        <span class="font-medium">Points of Contact:</span>
                                    </div>
                                    <div class="related-customers-list">
                                        {#each nonPrimaryCustomers as customer (customer.id)}
                                            <div class="related-customer-row">
                                                <div class="customer-info">
                                                    <span class="customer-name">
                                                        {customer.type === 'organisation' ? customer.organisation_name : `${customer.first_name || ''} ${customer.last_name || ''}`.trim()}
                                                    </span>
                                                    <span class="customer-type-badge" class:org={customer.type === 'organisation'}>
                                                        {customer.type === 'organisation' ? 'Organisation' : 'Contact'}
                                                    </span>
                                                </div>
                                                <div class="customer-contact">
                                                    {#if customer.email}
                                                        <span class="contact-item">
                                                            <Mail class="h-3.5 w-3.5" />
                                                            {customer.email}
                                                        </span>
                                                    {/if}
                                                    {#if customer.phone}
                                                        <span class="contact-item">
                                                            <Phone class="h-3.5 w-3.5" />
                                                            {customer.phone}
                                                        </span>
                                                    {/if}
                                                </div>
                                            </div>
                                        {/each}
                                    </div>
                                </div>
                            {/if}
                        </FormBlock>
                {/each}

                <PhasesList
                    recordType="lead"
                    recordId={lead.id}
                    phases={leadPhases}
                    onRecordUpdated={(updatedLead) => {
                        lead = { ...updatedLead, comments: lead.comments };
                        if (onLeadUpdated) onLeadUpdated(lead);
                    }}
                />

                <!-- Site Survey -->
                {#if siteSurveyBlocks && siteSurveyBlocks.length > 0}
                    <SiteSurveyForm
                        record={lead}
                        recordType="lead"
                        siteSurveyBlocks={siteSurveyBlocks}
                        siteSurveyDependencyMap={siteSurveyDependencyMap}
                        siteSurveyImages={siteSurveyImages}
                        compact={true}
                        onRecordUpdated={(updatedLead) => {
                            lead = { ...updatedLead, comments: lead.comments };
                            if (onLeadUpdated) onLeadUpdated(lead);
                        }}
                    />
                {/if}
            </FormContainer>

            <Gallery
                images={siteSurveyImages}
                relatedRecordId={lead.id}
                relatedRecordType="App\Models\Lead"
                csrfToken={$page.props.csrfToken}
                showSourceBadge={true}
            />

            <LineItems
                relatedRecordId={lead.id}
                relatedRecordType="App\Models\Lead"
                lineItems={lead.lineItems}
            />

            {#if siteVisitBlocks}
                <SiteVisitsList
                    {siteVisits}
                    visitableType="App\Models\Lead"
                    visitableId={lead.id}
                    blocks={siteVisitBlocks}
                />
            {/if}

            {#if documentBlocks && relatedDocuments}
                <RelatedDocuments {relatedDocuments} relatedRecordId={lead.id} blocks={documentBlocks} onOpenCreate={openCreateDocumentModal}>
                    <CreateDocumentModal
                        blocks={documentBlocks}
                        bind:show={showCreateDocumentModal}
                        relatedRecordType="App\Models\Lead"
                        relatedRecordId={lead.id}
                        relatedRecordLabel={lead.first_name + ' ' + lead.last_name}
                    />
                </RelatedDocuments>
            {/if}

            <Comments
                commentableId={lead.id}
                commentableType="App\Models\Lead"
                comments={Array.isArray(lead.comments) ? lead.comments : []}
                currentUser={$page.props.auth.user}
                reloadKey="leads"
            />
        </div>
    </Modal>

{/if}

<style>
    .lead-detail-content {
        display: flex;
        flex-direction: column;
        gap: 1.5rem;
        max-height: 70vh;
        overflow-y: auto;
        padding: 0.5rem;
    }

    .related-customers-section {
        margin-top: 0.5rem;
    }

    .related-customers-header {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        margin-bottom: 0.5rem;
    }

    .related-customers-list {
        display: flex;
        flex-direction: column;
        gap: 0.75rem;
        padding-left: 0.5rem;
    }

    .related-customer-row {
        display: flex;
        flex-direction: column;
        gap: 0.25rem;
        padding-bottom: 0.75rem;
        border-bottom: 1px solid var(--clr-border);
    }

    .related-customer-row:last-child {
        border-bottom: none;
        padding-bottom: 0;
    }

    .customer-info {
        display: flex;
        align-items: center;
        gap: 0.5rem;
    }

    .customer-name {
        font-weight: 500;
    }

    .customer-type-badge {
        font-size: 0.625rem;
        font-weight: 500;
        padding: 0.125rem 0.375rem;
        border-radius: 9999px;
        text-transform: uppercase;
        letter-spacing: 0.025em;
        background: hsl(var(--muted-foreground) / 0.1);
        color: var(--muted-foreground);
    }

    .customer-type-badge.org {
        background: hsl(var(--primary) / 0.1);
        color: hsl(var(--primary));
    }

    .customer-contact {
        display: flex;
        flex-wrap: wrap;
        gap: 1rem;
    }

    .contact-item {
        display: flex;
        align-items: center;
        gap: 0.375rem;
        font-size: 0.875rem;
        color: var(--muted-foreground);
    }

    .contact-item :global(svg) {
        opacity: 0.7;
    }
</style>
